import React, { useState, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import {
  Table,
  Row,
  Col,
  Input,
  DatePicker,
  Button,
  Space,
  Tooltip,
  Popconfirm,
  Dropdown,
  Menu,
} from "antd";

import { ColumnsType } from "antd/lib/table";
import { PagedResult } from "states/paged-result-model";
import { useDebouncedCallback } from "use-debounce/lib";

import {
  SearchOrderRequestModel,
  OrderModel,
  OrderItemModel,
  OrderPaymentModel,
  OrderProcessingModel,
  OrderPackingListModel,
  OrderConst,
  getOrderItemPriceWithModifierTotal,
} from "states/order/order.models";
import { exportOrder, getOrderById, searchOrder } from "states/order/order.api";
import momentTZ from "moment-timezone";
import { OrderStatusSelector } from "./components/order-status-selector";
import {
  momentDateFormat,
  momentDateTimeFormat,
} from "shared/plugins/localization";
import { RemovedEntityModel } from "shared/models/removed-entity-model";
import { OrderStatusTag } from "./components/order-status-tag";
import {
  CloudDownloadOutlined,
  MoreOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { formatMoney, formatPersonName } from "shared/plugins/string-helper";
import { ProductName } from "shared/components/product/product-name";
import { StaffSelector } from "shared/components/staff/staff-selector";
import { StaffModel } from "states/staff/staff.models";
import { fetchStaffList } from "states/staff/staff.slice";
import { OrderPaymentInfoModal } from "./components/order-payment-info-modal";
import { OrderProcessingModal } from "./components/order-processing-modal";
import { selectCurrentBranch } from "states/branch/branch.slice";
import { setLoading } from "states/global-value/global-value.slice";
import { getGlobalThemeSettingApi } from "states/theme-setting/theme-setting.api";
import { printTheOrder } from "shared/hooks/use-printer";
import { OrderActions } from "./components/order-actions";
import { OrderDetailInfo } from "./components/order-detail-info";
import { OrderPaymentInfo } from "./components/order-payment-info";
import { OrderPackingListInfo } from "./components/order-packing-list-info";
import {
  IOrderPackingListModalProps,
  OrderPackingListModal,
} from "./components/order-packing-list-modal";

interface IOrderProcessingModal {
  openModal: boolean;
  closeModal: () => void;
  processing: OrderProcessingModel[];
}
interface IOrderPaymentInfoModal {
  openModal: boolean;
  closeModal: () => void;
  paymentInfos: OrderPaymentModel[];
}

const OrderPage = () => {
  const [selectedRowsKeys, setSelectedRowsKeys] = useState<
    RemovedEntityModel<string>
  >({
    idList: [],
  });
  const [expandedRowsKeys, setExpandedRowsKeys] = useState<React.Key[]>([]);

  const dispatch = useDispatch();
  const currentBranch = useSelector(selectCurrentBranch, shallowEqual);

  const [isLoadingList, setIsLoadingList] = useState(false);
  const [pagedResults, setPagedResults] = useState(
    {} as PagedResult<OrderModel>
  );

  const [logoPrint, setLogoPrint] = useState<string>();

  const [filterModel, setFilterModel] = useState<SearchOrderRequestModel>({
    branchId: currentBranch?.id ?? 0,
    page: 1,
    pageSize: 20,
    orderStatus: [],
  } as SearchOrderRequestModel);
  const rowSelection = {
    selectedRowsKeys,
    onChange: (selectedRowKeys: any) => {
      setSelectedRowsKeys({ ...selectedRowsKeys, idList: selectedRowKeys });
    },
  };

  const [paymentInfoModal, setPaymentInfoModal] =
    useState<IOrderPaymentInfoModal>({
      openModal: false,
      closeModal: () => {
        setPaymentInfoModal({
          ...paymentInfoModal,
          openModal: false,
          paymentInfos: [],
        });
      },
      paymentInfos: [],
    });

  const [processingModal, setProcessingModal] = useState<IOrderProcessingModal>(
    {
      openModal: false,
      closeModal: () => {
        setProcessingModal({
          ...processingModal,
          openModal: false,
          processing: [],
        });
      },
      processing: [],
    }
  );

  const loadOrder = async (orderId: string) => {
    let result = await getOrderById(orderId);
    let orderList = [...pagedResults.results];
    let orderIndex = orderList.findIndex((m) => m.id === result.data.id);
    if (orderIndex < 0) return;
    orderList[orderIndex] = result.data;
    setPagedResults({ ...pagedResults, results: orderList });
  };

  const [shippingModal, setShippingModal] =
    useState<IOrderPackingListModalProps>({
      openModal: false,
      closeModal: () => {
        setShippingModal({
          ...shippingModal,
          openModal: false,
          orderPackingListInfo: {} as OrderPackingListModel,
        });
      },
      orderPackingListInfo: {} as OrderPackingListModel,
      onSuccess: async () => {},
      updateMode: false,
      onChangeModal: async (newModal: IOrderPackingListModalProps) => {
        setShippingModal(newModal);
      },
    });

  const gotoPage = async (page: number,pageSize:number) => {
    setIsLoadingList(true);
    const result = await searchOrder({
      ...filterModel,
      page,
      pageSize,
      branchId: currentBranch?.id ?? 0,
    });
    setPagedResults(result.data);
    setIsLoadingList(false);
  };

  const handleCreatePackingList = (model: OrderPackingListModel) => {
    setShippingModal({
      ...shippingModal,
      openModal: true,
      orderPackingListInfo: model,
      updateMode: false,
    });
  };
  const handleExportOrder = async () => {
    dispatch(setLoading(true));
    try {
      let result = await exportOrder({ ...filterModel });
      console.log(result.data);
      await saveByteArray("order.xlsx", result.data);
      dispatch(setLoading(false));
    } catch (e) {
      dispatch(setLoading(false));
    }
  };
  const saveByteArray = async (reportName: string, data: any) => {
    var link = document.createElement("a");
    link.href =
      "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64," +
      data;
    var fileName = reportName;
    link.download = fileName;
    link.click();
  };
  const handleSearch = useDebouncedCallback(() => {
    gotoPage(1,20);
  }, 500);
  const columns: ColumnsType<OrderModel> = [
    {
      title: "Mã đơn hàng",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      render: (invoiceNumber: string, item) => (
        <React.Fragment>
          <Button
            type="link"
            onClick={() => {
              if (!expandedRowsKeys.find((m) => m === item.id)) {
                setExpandedRowsKeys([item.id]);
              } else {
                setExpandedRowsKeys([]);
              }
            }}
          >
            {item.orderNumber}{" "}
            {item.note && (
              <Tooltip overlay={item.note}>
                <span className="text-danger">
                  <WarningOutlined />
                </span>
              </Tooltip>
            )}
          </Button>
        </React.Fragment>
      ),
    },
    {
      title: "Ngày tạo",
      dataIndex: "creationTime",
      key: "creationTime",
      render: (creationTime: Date) => (
        <React.Fragment>
          {momentTZ
            .tz(creationTime, currentBranch?.ianaTimeZone)
            .format(momentDateTimeFormat())}
        </React.Fragment>
      ),
    },
    {
      title: "Khách hàng",
      dataIndex: "customer",
      key: "customer",
      render: (supplier, item) =>
        item.customer
          ? formatPersonName(item.customer?.firstName, item.customer?.lastName)
          : "",
    },
    {
      title: "Trạng thái",
      dataIndex: "orderStatus",
      key: "orderStatus",
      render: (status: string, item) => <OrderStatusTag status={item.status} />,
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (printBill: string, item) => (
        <Space>
          {/* <Popconfirm
            title={"Bạn muốn in đơn hàng không?"}
            onConfirm={(e) => {
              printTheOrder(item, logoPrint ?? "");
            }}
          >
            <Button type="primary">Print</Button>
          </Popconfirm> */}
          {item.status !== OrderConst.status.completed &&
            item.status !== OrderConst.status.cancel &&
            item.status !== OrderConst.status.returned && (
              <Dropdown
                trigger={["click"]}
                overlay={
                  <Menu className="bg-white p-2">
                    <OrderActions
                      order={item}
                      handleCreatePackingList={handleCreatePackingList}
                      loadOrder={loadOrder}
                    />
                  </Menu>
                }
              >
                <Button icon={<MoreOutlined />} />
              </Dropdown>
            )}
        </Space>
      ),
    },
  ];
  const orderItemColumns: ColumnsType<OrderItemModel> = [
    {
      title: "#",
      dataIndex: "stt",
      key: "stt",
      width: "50px",
      render: (stt: string, item, index) => <>{index + 1}</>,
    },
    {
      title: "SKU",
      dataIndex: "skuCode",
      key: "skuCode",
      width: "120px",
      render: (skuCode: string, item) => item.product.skuCode,
    },
    {
      title: "Tên sản phẩm",
      dataIndex: "name",
      key: "name",
      render: (name: string, item) => (
        <>
          <ProductName product={item.product} />
          <br />
          {item.modifiers.map((mod, index) => (
            <>
              {index > 0 && ", "}
              <ProductName product={mod.product} />
            </>
          ))}{" "}
        </>
      ),
    },
    {
      title: "SL",
      dataIndex: "quantity",
      key: "quantity",
      width: "100px",
      render: (quantity: string, item) => item.quantity,
    },
    {
      title: "Tạm tính",
      dataIndex: "total",
      key: "total",
      width: "150px",
      render: (name: string, item) => (
        <React.Fragment>
          {formatMoney(getOrderItemPriceWithModifierTotal(item))}
        </React.Fragment>
      ),
    },
  ];

  const expandedRowOrder = (order: OrderModel) => {
    return (
      <Row
        gutter={[8, 8]}
        style={{ backgroundColor: "#d6d6d6" }}
        className="p-2"
      >
        <Col
          span={16}
          xl={{ span: 16 }}
          lg={{ span: 16 }}
          md={{ span: 24 }}
          sm={{ span: 24 }}
          xs={{ span: 24 }}
        >
          <Table
            columns={orderItemColumns}
            dataSource={order.orderItems}
            size="small"
            pagination={false}
            scroll={{ y: 300 }}
            className="table-m-0"
          />
        </Col>
        <Col
          span={8}
          xl={{ span: 8 }}
          lg={{ span: 8 }}
          md={{ span: 24 }}
          sm={{ span: 24 }}
          xs={{ span: 24 }}
        >
          <OrderDetailInfo
            order={order}
            onViewOrAdd={() => {
              setProcessingModal({
                ...processingModal,
                openModal: true,
                processing: order.orderProcessings,
              });
            }}
          />
          {order.orderPackingLists?.map((packingList) => (
            <OrderPackingListInfo
              orderPackingList={packingList}
              onViewOrAdd={() => {}}
              onSuccess={loadOrder}
              onOpenReturnedModal={() => {}}
            />
          ))}

          <OrderPaymentInfo
            order={order}
            onViewOrAdd={() => {
              setPaymentInfoModal({
                ...paymentInfoModal,
                openModal: true,
                paymentInfos: order.orderPayments,
              });
            }}
          />
          <div>
            <OrderActions
              order={order}
              handleCreatePackingList={handleCreatePackingList}
              loadOrder={loadOrder}
            />
          </div>
        </Col>
      </Row>
    );
  };

  useEffect(() => {
    handleSearch();
  }, [filterModel, currentBranch]);

  useEffect(() => {
    document.title = "Orders";
    getGlobalThemeSettingApi().then((res) => {
      let logoPrint = res.data.value["datalogoprint"]["value"];
      setLogoPrint(logoPrint);
    });
    dispatch(fetchStaffList());
  }, []);

  return (
    <div className="orders">
      <Row gutter={[16, 16]} justify="space-between" className="mb-4">
        <Col span={4}>
          <Input.Search
            value={filterModel.keyword}
            placeholder="Nhập tên khách hàng, email, số điện thoại..."
            onChange={(e) => {
              setFilterModel({
                ...filterModel,
                keyword: e.target.value,
              });
            }}
          ></Input.Search>
        </Col>
        <Col span={4}>
          <StaffSelector
            value={filterModel.staffs ?? []}
            isMultiple={true}
            onChange={(value) => {
              let valueTemp = value as StaffModel[];
              setFilterModel({
                ...filterModel,
                staffs: valueTemp?.map((m) => m.id),
              });
            }}
          ></StaffSelector>
        </Col>
        <Col span={4}>
          <OrderStatusSelector
            orderStatus={filterModel?.orderStatus}
            onChange={(value: string[]) => {
              setFilterModel({ ...filterModel, orderStatus: value });
            }}
          />
        </Col>
        <Col span={4}>
          <DatePicker
            format={momentDateFormat()}
            className="w-100"
            placeholder={"Từ ngày"}
            onChange={(value, dateString) => {
              setFilterModel({
                ...filterModel,
                fromDate: momentTZ
                  .tz(
                    dateString,
                    momentDateFormat(),
                    currentBranch?.ianaTimeZone
                  )
                  .startOf("date")
                  .toDate(),
              });
            }}
          />
        </Col>
        <Col span={4}>
          <DatePicker
            format={momentDateFormat()}
            className="w-100"
            placeholder={"Đến ngày"}
            onChange={(value, dateString) => {
              if (value === null) return;
              setFilterModel({
                ...filterModel,
                toDate: momentTZ
                  .tz(
                    dateString,
                    momentDateFormat(),
                    currentBranch?.ianaTimeZone
                  )
                  .endOf("date")
                  .toDate(),
              });
            }}
          />
        </Col>
        <Col span={4} className="d-flex justify-content-end">
          {/* <Space>
            <Button
              onClick={handleExportOrder}
              danger
              type="primary"
              icon={<CloudDownloadOutlined />}
            >
              Export Order
            </Button>
          </Space> */}
        </Col>
      </Row>
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={pagedResults.results}
        size="small"
        pagination={{
          total: pagedResults.totalItemCount,
          pageSize: pagedResults.pageSize,
          current: pagedResults.currentPage,
          onChange: gotoPage,
        }}
        loading={isLoadingList}
        expandable={{
          expandedRowRender: expandedRowOrder,
          expandedRowKeys: expandedRowsKeys,
          onExpandedRowsChange: (ex) => {
            setExpandedRowsKeys([...ex]);
          },
        }}
        rowKey={(_record, index) => _record.id.toString() ?? ""}
      ></Table>
      <OrderProcessingModal
        processings={processingModal?.processing}
        closeModal={processingModal?.closeModal}
        onModal={processingModal?.openModal}
      />
      <OrderPaymentInfoModal
        payments={paymentInfoModal?.paymentInfos}
        closeModal={paymentInfoModal?.closeModal}
        onModal={paymentInfoModal?.openModal}
        onSuccess={loadOrder}
      />
      <OrderPackingListModal
        openModal={shippingModal.openModal}
        closeModal={shippingModal.closeModal}
        orderPackingListInfo={shippingModal.orderPackingListInfo}
        updateMode={shippingModal.updateMode}
        onSuccess={loadOrder}
        onChangeModal={shippingModal.onChangeModal}
      />
    </div>
  );
};

export default OrderPage;
